import { useState, useEffect, useMemo } from "react";
import {
  Inbox,
  ArrowUpRight,
  BookOpen,
  CheckCircle,
  XCircle,
  Eye,
  MousePointer,
  CheckSquare,
  Square,
  TrendingDown,
  Clock,
  RefreshCw,
  Building2,
  Link,
  Mail,
} from "lucide-react";
import { colors, fonts, CONTACT_TYPES, EMAIL_FREQUENCIES } from "../styles/theme";
import { SearchBar, Spinner, PageHeader, ErrorBanner } from "../components/ui";
import { ContactDrawer } from "../components/ContactDrawer";
import { useTenant } from "../contexts/TenantContext";
import { useAuth } from "../hooks/useAuth";
import {
  fetchContacts,
  fetchCompanies,
  updateContact,
  bulkUpdateContacts,
  sendWelcomeEmail,
  deriveContactType,
  displayName,
  initials,
  fmtDate,
} from "../services/api";

export default function ReviewQueue() {
  const { features } = useTenant();
  const { user } = useAuth();
  const [tab, setTab] = useState("pending"); // "pending" | "rereview"
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(null); // contactId or "bulk"
  const [sortBy, setSortBy] = useState("engagement"); // "engagement" | "newest"
  const [selectedContact, setSelectedContact] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [reReviewCount, setReReviewCount] = useState(0);
  const [companyLookup, setCompanyLookup] = useState({ byDomain: {}, byBrandRoot: [] });

  // Fetch companies and build domain + brandRoot lookup
  useEffect(() => {
    fetchCompanies()
      .then((data) => {
        const byDomain = {};
        const byBrandRoot = [];
        for (const co of data.companies || []) {
          for (const d of co.domains || []) {
            byDomain[d.toLowerCase()] = co;
          }
          if (co.brandRoot && co.brandRoot.length >= 4) {
            byBrandRoot.push({ root: co.brandRoot.toLowerCase(), company: co });
          }
        }
        // Sort by root length descending so longest (most specific) match wins
        byBrandRoot.sort((a, b) => b.root.length - a.root.length);
        setCompanyLookup({ byDomain, byBrandRoot });
      })
      .catch(() => {});
  }, []);

  // Fetch badge counts for both tabs on mount
  useEffect(() => {
    fetchContacts({ reviewStatus: "pending", limit: 1 })
      .then((data) => setPendingCount(data.total || 0))
      .catch(() => {});
    fetchContacts({ reviewStatus: "due", limit: 1 })
      .then((data) => setReReviewCount(data.total || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const reviewStatus = tab === "rereview" ? "due" : "pending";

    const timer = setTimeout(
      () => {
        fetchContacts({ reviewStatus, limit: 200, search, sort: "createdAt" })
          .then((data) => {
            if (cancelled) return;
            setContacts(data.contacts || []);
            setTotal(data.total || 0);
            setSelectedIds(new Set());
            // Update badge count for active tab
            if (tab === "pending") setPendingCount(data.total || 0);
            else setReReviewCount(data.total || 0);
          })
          .catch((err) => {
            if (cancelled) return;
            setError(err.message);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      search ? 300 : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, tab]);

  const removeContacts = (ids) => {
    const idSet = new Set(ids);
    setContacts((prev) => prev.filter((c) => !idSet.has(c.id)));
    setTotal((prev) => Math.max(0, prev - ids.length));
    if (tab === "pending") setPendingCount((prev) => Math.max(0, prev - ids.length));
    else setReReviewCount((prev) => Math.max(0, prev - ids.length));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  // ── Single-contact actions ──
  const handleAction = async (contact, action) => {
    setActionLoading(contact.id);
    try {
      const updates = {
        reviewStatus: "reviewed",
        reviewedAt: new Date().toISOString(),
        reviewedBy: user?.userDetails || "unknown",
        reviewAction: action,
      };
      if (action === "prospect") {
        const newTags = [...new Set([...(contact.tags || []), "prospect"])];
        await updateContact(contact.id, { email: contact.email, ...updates, tags: newTags });
      } else if (action === "digest") {
        await updateContact(contact.id, { email: contact.email, ...updates, emailFrequency: "digest" });
      } else if (action === "dismiss") {
        await updateContact(contact.id, { email: contact.email, ...updates, unsubscribed: true });
      } else {
        // "keep" — just mark reviewed
        await updateContact(contact.id, { email: contact.email, ...updates });
      }
      removeContacts([contact.id]);
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleWelcome = async (contact) => {
    setActionLoading(contact.id);
    try {
      await sendWelcomeEmail(contact.id, contact.email);
      removeContacts([contact.id]);
    } catch (err) {
      alert(`Failed to send welcome email: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Bulk actions ──
  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    setActionLoading("bulk");

    const selected = contacts.filter((c) => selectedIds.has(c.id));
    const contactIds = selected.map((c) => ({ id: c.id, email: c.email }));

    try {
      const updates = {
        reviewStatus: "reviewed",
        reviewedAt: new Date().toISOString(),
        reviewedBy: user?.userDetails || "unknown",
        reviewAction: action,
      };
      let addTags = [];
      if (action === "prospect") addTags = ["prospect"];
      if (action === "digest") updates.emailFrequency = "digest";
      if (action === "dismiss") updates.unsubscribed = true;

      await bulkUpdateContacts(contactIds, updates, addTags);
      removeContacts([...selectedIds]);
    } catch (err) {
      alert(`Bulk update failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === sortedContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedContacts.map((c) => c.id)));
    }
  };

  const getEngagement = (c) => {
    const cf = c.customFields || {};
    return (Number(cf.emailOpensCount) || 0) + (Number(cf.emailClicksCount) || 0);
  };

  const handleContactUpdate = (updated) => {
    setContacts((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
    setSelectedContact((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const sortedContacts = useMemo(() => {
    const sorted = [...contacts];
    if (sortBy === "engagement") {
      sorted.sort((a, b) => getEngagement(a) - getEngagement(b));
    }
    // "newest" keeps the server-side createdAt DESC order
    return sorted;
  }, [contacts, sortBy]);

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Review Queue"
        subtitle={loading ? "Loading..." : total > contacts.length
          ? `Showing ${contacts.length} of ${total} contacts ${tab === "rereview" ? "due for re-review" : "pending review"}`
          : `${contacts.length} contacts ${tab === "rereview" ? "due for re-review" : "pending review"}`}
      />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        <TabButton active={tab === "pending"} onClick={() => { setTab("pending"); setSearch(""); }} label="New" count={pendingCount} Icon={Inbox} />
        <TabButton active={tab === "rereview"} onClick={() => { setTab("rereview"); setSearch(""); }} label="Re-review" count={reReviewCount} Icon={RefreshCw} />
      </div>

      {/* Search + Sort + Bulk bar */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <SearchBar value={search} onChange={setSearch} placeholder={tab === "rereview" ? "Search contacts due for re-review..." : "Search pending contacts..."} />
        <div style={{ display: "flex", gap: 4 }}>
          <SortButton
            active={sortBy === "engagement"}
            onClick={() => setSortBy("engagement")}
            Icon={TrendingDown}
            label="Lowest engagement"
          />
          <SortButton
            active={sortBy === "newest"}
            onClick={() => setSortBy("newest")}
            Icon={Clock}
            label="Newest first"
          />
        </div>
        {selectedIds.size > 0 && (
          <div style={{
            display: "flex",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 8,
            background: colors.bgActive,
            border: `1px solid ${colors.border}`,
            alignItems: "center",
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginRight: 4 }}>
              {selectedIds.size} selected:
            </span>
            {BULK_ACTIONS.map((a) => (
              <BulkActionButton
                key={a.action}
                {...a}
                disabled={actionLoading === "bulk"}
                onClick={() => handleBulkAction(a.action)}
              />
            ))}
          </div>
        )}
      </div>

      {error && (
        <ErrorBanner message={`Failed to load contacts: ${error}`} style={{ marginBottom: 16 }} />
      )}

      {/* Select All header */}
      {!loading && sortedContacts.length > 0 && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          marginBottom: 8,
        }}>
          <button
            onClick={toggleAll}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}
          >
            {selectedIds.size === sortedContacts.length ? (
              <CheckSquare size={16} color={colors.accent} />
            ) : (
              <Square size={16} color={colors.textSecondary} />
            )}
          </button>
          <span style={{ fontSize: 12, color: colors.textSecondary }}>
            {selectedIds.size === sortedContacts.length ? "Deselect all" : "Select all"}
          </span>
        </div>
      )}

      {/* Contact cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: colors.textSecondary }}>
          <Spinner size={24} />
        </div>
      ) : sortedContacts.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: 60,
          background: colors.bgCard,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
        }}>
          <Inbox size={40} color={colors.textSecondary} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: fonts.heading, color: colors.text, marginBottom: 4 }}>
            {tab === "rereview" ? "No re-reviews due" : "Queue is clear"}
          </div>
          <div style={{ fontSize: 13, color: colors.textSecondary }}>
            {search
              ? "No contacts match your search."
              : tab === "rereview"
                ? "No contacts are due for re-review. Configure re-review cycles in Settings."
                : "No new contacts pending review. New contacts will appear here automatically."}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sortedContacts.map((contact) => (
            <ReviewCard
              key={contact.id}
              contact={contact}
              selected={selectedIds.has(contact.id)}
              onToggleSelect={() => toggleSelect(contact.id)}
              onAction={(action) => handleAction(contact, action)}
              onWelcome={() => handleWelcome(contact)}
              onOpenDrawer={() => setSelectedContact(contact)}
              loading={actionLoading === contact.id}
              isReReview={tab === "rereview"}
              companyLookup={companyLookup}
              onAssignCompany={handleContactUpdate}
            />
          ))}
        </div>
      )}

      <ContactDrawer
        contact={selectedContact}
        onClose={() => setSelectedContact(null)}
        onContactUpdate={handleContactUpdate}
      />
    </div>
  );
}

const BULK_ACTIONS = [
  { action: "prospect", label: "Prospect", color: "#f59e0b", Icon: ArrowUpRight },
  { action: "digest", label: "Digest", color: "#8b5cf6", Icon: BookOpen },
  { action: "keep", label: "Keep", color: "#10b981", Icon: CheckCircle },
  { action: "dismiss", label: "Dismiss", color: "#ef4444", Icon: XCircle },
];

function TabButton({ active, onClick, label, count, Icon }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 600,
        fontFamily: fonts.body,
        borderRadius: 8,
        border: `1px solid ${active ? colors.accent + "44" : colors.border}`,
        background: active ? colors.accent + "12" : "transparent",
        color: active ? colors.accent : colors.textSecondary,
        cursor: "pointer",
        transition: "all 0.15s",
      }}
    >
      <Icon size={14} />
      {label}
      {count > 0 && (
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          fontFamily: fonts.mono,
          padding: "1px 7px",
          borderRadius: 10,
          background: active ? colors.accent + "20" : colors.bgActive,
          color: active ? colors.accent : colors.textSecondary,
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

function SortButton({ active, onClick, Icon, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "6px 12px",
        fontSize: 11,
        fontWeight: 600,
        fontFamily: fonts.body,
        borderRadius: 6,
        border: `1px solid ${active ? colors.accent + "66" : colors.border}`,
        background: active ? colors.accent + "15" : "transparent",
        color: active ? colors.accent : colors.textSecondary,
        cursor: "pointer",
        transition: "all 0.15s",
      }}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

function BulkActionButton({ label, color, Icon, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 10px",
        fontSize: 11,
        fontWeight: 600,
        fontFamily: fonts.body,
        borderRadius: 5,
        border: `1px solid ${color}33`,
        background: `${color}12`,
        color,
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "all 0.15s",
      }}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

const ACTION_LABELS = { prospect: "Prospect", digest: "Digest", keep: "Keep", dismiss: "Dismiss", welcome: "Welcome Sent" };

// Strip TLD to get SLD — handles compound TLDs like co.uk, co.jp
const COMPOUND_TLDS = ["co.uk","co.jp","co.kr","co.in","co.nz","co.za","co.il","co.th","com.au","com.br","com.sg","com.hk","com.my","com.tw","com.cn","com.mx","com.ar","com.tr","org.uk","org.au","net.au","ac.uk","ac.jp","ne.jp","or.jp","go.jp","gov.uk"];
function stripTLD(domain) {
  const d = domain.toLowerCase();
  for (const tld of COMPOUND_TLDS) {
    if (d.endsWith("." + tld)) return d.slice(0, -(tld.length + 1));
  }
  const lastDot = d.lastIndexOf(".");
  return lastDot > 0 ? d.slice(0, lastDot) : d;
}

function findCompanySuggestion(emailDomain, companyLookup) {
  if (!emailDomain) return null;
  // 1. Exact domain match (most reliable)
  const exact = companyLookup.byDomain[emailDomain];
  if (exact) return exact;
  // 2. Brand root match — extract SLD, find longest matching brandRoot prefix
  const sld = stripTLD(emailDomain).split("-")[0]; // first segment before hyphen
  if (sld.length < 4) return null;
  for (const { root, company } of companyLookup.byBrandRoot) {
    if (sld.startsWith(root) || root.startsWith(sld)) return company;
  }
  return null;
}

const WELCOME_SOURCES = ["newsletter-signup", "signup", "landing-page", "contact-form"];

function ReviewCard({ contact, selected, onToggleSelect, onAction, onWelcome, onOpenDrawer, loading, isReReview, companyLookup, onAssignCompany }) {
  const type = deriveContactType(contact.tags, contact.ghlTags);
  const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
  const cf = contact.customFields || {};
  const opens = Number(cf.emailOpensCount) || 0;
  const clicks = Number(cf.emailClicksCount) || 0;
  const createdDate = contact.createdAt ? fmtDate(contact.createdAt) : "—";
  const [assigning, setAssigning] = useState(false);

  // Find suggested company by domain or brand root match
  const emailDomain = contact.email?.split("@")[1]?.toLowerCase();
  const suggestedCompany = !contact.companyId ? findCompanySuggestion(emailDomain, companyLookup) : null;

  const handleAssign = async (e) => {
    e.stopPropagation();
    if (!suggestedCompany) return;
    setAssigning(true);
    try {
      const updated = await updateContact(contact.id, {
        email: contact.email,
        companyId: suggestedCompany.id,
        company: suggestedCompany.name,
      });
      onAssignCompany(updated);
    } catch (err) {
      alert(`Failed to assign company: ${err.message}`);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "14px 16px",
      background: selected ? colors.bgActive : colors.bgCard,
      border: `1px solid ${selected ? colors.accent + "44" : colors.border}`,
      borderRadius: 10,
      transition: "all 0.15s",
      opacity: loading ? 0.5 : 1,
    }}>
      {/* Checkbox */}
      <button
        onClick={onToggleSelect}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", flexShrink: 0 }}
      >
        {selected ? (
          <CheckSquare size={16} color={colors.accent} />
        ) : (
          <Square size={16} color={colors.textSecondary} />
        )}
      </button>

      {/* Avatar + Info — clickable to open drawer */}
      <div
        onClick={onOpenDrawer}
        style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, cursor: "pointer" }}
      >
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: typeInfo.bg,
          color: typeInfo.color,
          fontFamily: fonts.heading,
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
        }}>
          {initials(contact)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: colors.text }}>
              {displayName(contact)}
            </span>
            <span style={{
              fontSize: 10,
              padding: "1px 6px",
              borderRadius: 3,
              background: typeInfo.bg,
              color: typeInfo.color,
              fontWeight: 600,
            }}>
              {typeInfo.label}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: colors.textSecondary }}>
            <span style={{ fontFamily: fonts.mono, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {contact.email}
            </span>
            {contact.company && (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {contact.company}
              </span>
            )}
          </div>
          {suggestedCompany && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
              <Building2 size={11} color={colors.accent} />
              <span style={{ fontSize: 11, color: colors.textSecondary }}>
                Suggested: <strong style={{ color: colors.text }}>{suggestedCompany.name}</strong>
              </span>
              <button
                onClick={handleAssign}
                disabled={assigning}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  padding: "1px 8px",
                  fontSize: 10,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                  borderRadius: 4,
                  border: `1px solid ${colors.accent}44`,
                  background: `${colors.accent}15`,
                  color: colors.accent,
                  cursor: assigning ? "wait" : "pointer",
                  opacity: assigning ? 0.5 : 1,
                }}
              >
                {assigning ? <Spinner size={10} /> : <Link size={10} />}
                {assigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          )}
          {isReReview && contact.reviewedAt && (
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
              Last reviewed {fmtDate(contact.reviewedAt)}
              {contact.reviewedBy ? ` by ${contact.reviewedBy}` : ""}
              {contact.reviewAction ? ` — ${ACTION_LABELS[contact.reviewAction] || contact.reviewAction}` : ""}
            </div>
          )}
        </div>
      </div>

      {/* Engagement stats */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
        {(opens > 0 || clicks > 0) && (
          <>
            <span title="Opens" style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, fontFamily: fonts.mono, color: opens > 0 ? colors.success : colors.textSecondary }}>
              <Eye size={11} /> {opens}
            </span>
            <span title="Clicks" style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, fontFamily: fonts.mono, color: clicks > 0 ? colors.accent : colors.textSecondary }}>
              <MousePointer size={11} /> {clicks}
            </span>
          </>
        )}
        <span style={{ fontSize: 11, fontFamily: fonts.mono, color: colors.textSecondary, flexShrink: 0 }}>
          {createdDate}
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        {WELCOME_SOURCES.includes(contact.source) && !contact.welcomeEmailSentAt && (
          <ActionButton
            title="Approve & Send Welcome"
            color="#2563eb"
            Icon={Mail}
            disabled={loading}
            onClick={onWelcome}
          />
        )}
        <ActionButton
          title="Promote to Prospect"
          color="#f59e0b"
          Icon={ArrowUpRight}
          disabled={loading}
          onClick={() => onAction("prospect")}
        />
        <ActionButton
          title="Move to Monthly Digest"
          color="#8b5cf6"
          Icon={BookOpen}
          disabled={loading}
          onClick={() => onAction("digest")}
        />
        <ActionButton
          title="Keep as Subscriber"
          color="#10b981"
          Icon={CheckCircle}
          disabled={loading}
          onClick={() => onAction("keep")}
        />
        <ActionButton
          title="Dismiss (unsubscribe)"
          color="#ef4444"
          Icon={XCircle}
          disabled={loading}
          onClick={() => onAction("dismiss")}
        />
      </div>
    </div>
  );
}

function ActionButton({ title, color, Icon, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 30,
        height: 30,
        borderRadius: 6,
        border: `1px solid ${color}33`,
        background: "transparent",
        color,
        cursor: disabled ? "wait" : "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${color}15`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <Icon size={14} />
    </button>
  );
}
